import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, GraduationCap, Loader2 } from "lucide-react";

/**
 * Admin-only instructor routing. Course assignment is the single source of
 * truth for which instructor teaches a lesson video. Changing an assignment
 * affects future generations only — existing videos are never touched.
 */

type Row = Record<string, unknown>;

interface Console {
  instructors: Row[];
  course_assignments: Row[];
  unassigned_courses: Row[];
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("generation-orchestrator", { body });
  if (error) throw error;
  if (data?.error) throw new Error(String(data.error));
  return data as T;
}

function useSignedThumb(path: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!path) {
      setUrl(null);
      return;
    }
    const objectPath = path.startsWith("content-generation/") ? path.slice("content-generation/".length) : path;
    call<{ url: string }>({ action: "signed_asset_url", path: objectPath })
      .then((r) => !cancelled && setUrl(r.url))
      .catch(() => !cancelled && setUrl(null));
    return () => {
      cancelled = true;
    };
  }, [path]);
  return url;
}

function InstructorCard({
  instructor,
  courseCount,
  onToggleActive,
}: {
  instructor: Row;
  courseCount: number;
  onToggleActive: () => void;
}) {
  const thumb = useSignedThumb((instructor.canonical_image_path as string) ?? null);
  const active = instructor.is_active !== false;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border p-3">
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-muted">
        {thumb
          ? <img src={thumb} alt={`${String(instructor.display_name)} canonical reference`} className="h-full w-full object-cover" />
          : <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">no image</div>}
      </div>
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-medium">{String(instructor.display_name)}</div>
        <div className="text-xs text-muted-foreground">
          {String(instructor.discipline ?? "discipline not set")} · {String(instructor.instructor_key)}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          Voice {instructor.voice_id ? String(instructor.voice_id).slice(0, 8) + "…" : "missing"} ·{" "}
          {courseCount} course{courseCount === 1 ? "" : "s"}
        </div>
        {!instructor.canonical_image_path && (
          <div className="mt-1 text-xs text-destructive">Canonical image missing</div>
        )}
      </div>
      <Button size="sm" variant={active ? "secondary" : "outline"} onClick={onToggleActive}>
        {active ? "Active" : "Inactive"}
      </Button>
    </div>
  );
}

export default function InstructorRoutingPanel() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["cg-instructors"],
    queryFn: () => call<Console>({ action: "instructor_console" }),
    staleTime: 15_000,
  });

  const act = useMutation({
    mutationFn: (body: Record<string, unknown>) => call<Row>(body),
    onSuccess: () => {
      toast.success("Saved — affects future generations only.");
      qc.invalidateQueries({ queryKey: ["cg-instructors"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const instructors = data?.instructors ?? [];
  const assignments = data?.course_assignments ?? [];
  const unassigned = data?.unassigned_courses ?? [];

  return (
    <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-4">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
        <GraduationCap className="h-4 w-4" /> Instructor routing
      </h2>
      <p className="text-sm text-muted-foreground">
        Every lesson video takes its instructor from the lesson's course. Identity is never inferred from
        lesson text. Changing an assignment applies to future generations only.
      </p>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading instructors…
        </div>
      )}
      {isError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>{(error as Error).message}</div>
        </div>
      )}

      {data && (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            {instructors.map((i) => (
              <InstructorCard
                key={String(i.presenter_id)}
                instructor={i}
                courseCount={assignments.filter((c) => c.instructor_id === i.presenter_id).length}
                onToggleActive={() =>
                  act.mutate({
                    action: "set_instructor_active",
                    presenter_id: i.presenter_id,
                    is_active: i.is_active === false,
                  })}
              />
            ))}
          </div>

          {unassigned.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
              <span className="font-medium">Instructor assignment required:</span>{" "}
              {unassigned.map((c) => String(c.course_name)).join(", ")}
            </div>
          )}

          <div className="space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Course assignments ({assignments.length})
            </p>
            <div className="max-h-96 space-y-1 overflow-y-auto pr-1">
              {assignments.map((c) => (
                <div
                  key={String(c.course_id)}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm"
                >
                  <div className="min-w-56 flex-1 font-medium">{String(c.course_name)}</div>
                  {c.discipline ? <Badge variant="outline">{String(c.discipline)}</Badge> : null}
                  {c.assignment_required ? (
                    <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-600">
                      Instructor assignment required
                    </Badge>
                  ) : null}
                  <select
                    className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                    value={String(c.instructor_id ?? "")}
                    onChange={(e) =>
                      act.mutate({
                        action: "set_course_instructor",
                        course_id: c.course_id,
                        instructor_id: e.target.value,
                      })}
                  >
                    <option value="" disabled>Select instructor…</option>
                    {instructors.map((i) => (
                      <option key={String(i.presenter_id)} value={String(i.presenter_id)}>
                        {String(i.display_name)}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
